import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { KeyHeader } from '../../shared/TableComponent';

import { BarChart2, Plus } from 'lucide-react';
import { useTranslate } from 'sbx-crm-menu-ui';

import MarkdownPreview from '@uiw/react-markdown-preview';
import { useNavigate } from 'react-router-dom';
import TableComponentV2 from 'src/components/shared/TableComponentV2';
import { getAllJobsService } from 'src/services/DocumentOrImageV2Service';
import { appActions } from 'src/store';
import { ModalType } from 'src/types/Modal';
import { AppAgentContext } from '../../../context/ContextProvider';
import { DateUtil } from '../../../utils';
import NavbarBreadcrumbV2 from '../../shared/NavbarBreadcrumbV2';
import { Button } from '../../ui/button';
import { Permissions, SPEECH_TO_TEXT_V2_CREATE } from 'src/types/Permission';
import Permission from 'src/components/shared/Permission';
import { DeniedComponent } from 'src/App';

const SPEECH_V2 = Permissions.SBX_STUDIO_SPEECH_TO_TEXT_V2;

const SpeechToTextListComponentV2 = () => {
    const {
        state: {
            session: { user_id },
        },
    } = useContext(AppAgentContext);
    const { t: common } = useTranslate('common');
    const { t: speech } = useTranslate('speech');
    const { data, isLoading, isRefetching } = useQuery({
        queryKey: ['speech'],
        queryFn: async () => await getAllJobsService({ user: user_id, key_filter: 'speech_to_text', job_type: 'Chat' }),
    });
    const { dispatch } = useContext(AppAgentContext);
    // const [dateFrom, setDateFrom] = useState<Date>();

    const dataSort = useMemo(() => {
        if (data?.success) {
            return data.items.sort((a, b) => {
                const utcDateA = !a.created_at.includes('Z') ? a.created_at + 'Z' : a.created_at;
                const utcDateB = !b.created_at.includes('Z') ? b.created_at + 'Z' : b.created_at;
                return new Date(utcDateB).getTime() - new Date(utcDateA).getTime();
            });
        }
        return [];
    }, [data]);

    const navigate = useNavigate();

    return (
        <Permission permission={SPEECH_V2} denied={<DeniedComponent />}>
            <div className="min-h-screen bg-gradient-to-br from-fuchsia-100 via-sky-100 to-violet-200 p-4 sm:p-8">
                <div className="max-w-6xl mx-auto">
                    <NavbarBreadcrumbV2
                        routes={[
                            {
                                label: speech('speech_to_text_list'),
                                route: '',
                            },
                        ]}
                    />

                    <h1 className="title">{speech('speech_to_text_list')}</h1>

                    <TableComponentV2
                        items={dataSort}
                        loading={isLoading || isRefetching}
                        topActions={
                            <Permission permission={SPEECH_TO_TEXT_V2_CREATE}>
                                <div>
                                    <Button
                                        className="primary-gradient-button"
                                        onClick={() => {
                                            navigate('/speech-to-text-v2/transcribe');
                                        }}
                                    >
                                        <Plus size={20} className="mr-2" /> {speech('new_transcription')}
                                    </Button>
                                </div>
                            </Permission>
                        }
                        pagination
                        search
                        header={[
                            {
                                type: KeyHeader.KEY,
                                label: common('type'),
                                key: 'type',
                            },
                            {
                                type: KeyHeader.KEY,
                                label: common('status'),
                                key: 'status',
                            },
                            {
                                type: KeyHeader.VALUE,
                                label: common('createdDate'),
                                toValue: (item) => {
                                    if (item.created_at) {
                                        const utcDate = !item.created_at.includes('Z')
                                            ? item.created_at + 'Z'
                                            : item.created_at;
                                        return new DateUtil(new Date(utcDate), 'yyyy-MM-dd hh:mm:ss a').getDate();
                                    }
                                    return new DateUtil(new Date(), 'yyyy-MM-dd hh:mm:ss a').getDate();
                                },
                            },
                        ]}
                        actions={[
                            {
                                label: 'Ver Resultados',
                                icon: <BarChart2 className="h-4 w-4" />,
                                className:
                                    'h-8 w-8 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-600 flex items-center justify-center',
                                onClick: (item) => {
                                    dispatch(
                                        appActions.openModal({
                                            type: ModalType.DYNAMIC,
                                            data: {
                                                title: '',
                                                onClose: () => dispatch(appActions.closeModal(ModalType.DYNAMIC)),
                                                className:
                                                    'bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 rounded-3xl shadow-2xl max-w-[40vw]',
                                                children: (
                                                    <div className="bg-white p-2 rounded-3xl">
                                                        <h1 className="text-3xl text-center font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                                                            Respuesta
                                                        </h1>
                                                        <div className="py-4 px-4">
                                                            <MarkdownPreview
                                                                source={item.getResults('string')}
                                                                style={{ padding: 16 }}
                                                            />
                                                        </div>
                                                    </div>
                                                ),
                                            },
                                        })
                                    );
                                },
                            },
                        ]}
                    />
                </div>
            </div>
        </Permission>
    );
};

export default SpeechToTextListComponentV2;
